/**
 * mad-video — minimal player: native <video> + hls.js, muted autoplay with a tap-for-sound cue.
 * Ported from the MAD Videos plugin (MinimalPlayer.js). The controls are rendered by PHP
 * (templates/minimal-ui.php) and placed by js/slots.js; this class binds them and keeps the
 * state classes on the root up to date, which decide the visible icons (scss/_minimal.scss):
 *
 *   is-started, is-playing, is-paused, is-muted, is-ended, is-buffering, is-fullscreen,
 *   is-ui-visible (touch + auto-hide: controls shown for a moment after a tap)
 */

var attachSource = require('./media-source').attachSource;

var VISIBLE_RATIO = 0.5; // autoplay / resume once half of the video is visible
var HIDDEN_RATIO = 0.2;  // pause when less than this is visible
var UI_REVEAL_MS = 2500; // touch + auto-hide: how long a tap shows the controls

var STATE_CLASSES = ['is-started', 'is-playing', 'is-paused', 'is-muted', 'is-ended', 'is-buffering', 'is-fullscreen', 'is-ui-visible'];

var DEFAULT_LABELS = {
  video: 'Video',
  play: 'Play',
  pause: 'Pause',
  replay: 'Replay',
  mute: 'Mute',
  unmute: 'Turn sound on',
  fullscreen: 'Fullscreen',
  exitFullscreen: 'Exit fullscreen',
};

function noop() {}

function prefersReducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function saveDataEnabled() {
  return !!(navigator.connection && navigator.connection.saveData);
}

function parseLabels(ui) {
  try {
    return Object.assign({}, DEFAULT_LABELS, JSON.parse(ui.dataset.labels || '{}'));
  } catch (e) {
    return DEFAULT_LABELS;
  }
}

class MinimalPlayer {
  /**
   * @param {HTMLElement} root  The .mad-video element (with the PHP-rendered .mad-video__ui).
   * @param {Object}   options
   * @param {string}   options.url
   * @param {string}   [options.provider]             Provider resolved in PHP ('hls' | 'mp4')
   * @param {string}   [options.poster]
   * @param {number}   [options.volume=0.5]           Volume when sound is on (0–1)
   * @param {boolean}  [options.autoplay=false]       Muted autoplay once scrolled into view
   * @param {boolean}  [options.startWithSound=false] Start right away with sound (call from a click handler)
   * @param {Object}   [options.config]               data-player-config (build_minimal_config() in PHP)
   * @param {string}   [options.matomoTitle]
   * @param {string}   [options.matomoResource]
   * @param {Function} [options.onPlaying]            Video is playing (the overlay can fade out)
   * @param {Function} [options.onError]              The source can't be played
   */
  constructor(root, options) {
    var config = options.config || {};

    this.root = root;
    this.ui = root.querySelector('.mad-video__ui') || root;
    this.mount = root.querySelector('.mad-video__player');
    this.labels = parseLabels(this.ui);
    this.url = options.url;
    this.provider = options.provider;
    this.volume = typeof options.volume === 'number' ? Math.min(1, Math.max(0, options.volume)) : 0.5;
    this.onPlaying = options.onPlaying || noop;
    this.onError = options.onError || noop;

    this.loop = !!config.loop;
    this.unmuteCue = !!config.unmuteCue;
    this.restartOnUnmute = !!config.restartOnUnmute;
    this.pauseOffscreen = !!config.pauseOffscreen;
    this.seekable = !!config.seekable;
    this.autohide = !!config.autohide;

    var reducedMotion = !!config.respectReducedMotion && prefersReducedMotion();
    this.autoplay = !!options.autoplay && !reducedMotion && !saveDataEnabled();
    this.startWithSound = !!options.startWithSound;

    this.mutedByAutoplay = false; // playing muted because of autoplay → first unmute may restart
    this.pausedOffscreen = false; // paused by scrolling away → resume when visible again
    this.started = false;
    this.dragging = false;        // progress bar is being dragged
    this.lastPointerType = '';
    this.revealTimer = 0;
    this.progressFrame = 0;
    this.observer = null;
    this.events = new AbortController(); // removes every listener in destroy()

    this.findControls();
    this.createVideo(options);
    this.attach();
    this.bindEvents();

    if (this.startWithSound) {
      this.playWithSound();
    }
    if (this.autoplay || this.pauseOffscreen) {
      this.observeVisibility();
    }
    this.updateUi();
  }

  /* ─────────────── DOM ─────────────── */

  findControls() {
    var ui = this.ui;

    this.playButton = ui.querySelector('.mad-video__ctrl--play .mad-video__btn');
    this.muteButton = ui.querySelector('.mad-video__ctrl--sound .mad-video__btn');
    this.volumeSlider = ui.querySelector('.mad-video__volume');
    this.fullscreenCtrl = ui.querySelector('.mad-video__ctrl--fullscreen');
    this.fullscreenButton = this.fullscreenCtrl && this.fullscreenCtrl.querySelector('.mad-video__btn');
    this.progress = ui.querySelector('.mad-video__progress');
    this.centerPlay = ui.querySelector('.mad-video__center-play');
    this.cue = ui.querySelector('.mad-video__cue');
  }

  createVideo(options) {
    var video = document.createElement('video');

    video.className = 'mad-video__video';
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.loop = this.loop;
    video.volume = this.volume;
    video.tabIndex = 0;
    video.setAttribute('aria-label', options.matomoTitle || this.labels.video);
    if (options.poster) video.poster = options.poster;
    // Nothing is downloaded until needed, unless the video starts right away
    video.preload = this.autoplay || this.startWithSound ? 'metadata' : 'none';

    if (options.matomoTitle) {
      video.dataset.matomoTitle = options.matomoTitle;
      video.title = options.matomoTitle;
    }
    if (options.matomoResource) video.dataset.matomoResource = options.matomoResource;

    this.mount.innerHTML = '';
    this.mount.appendChild(video);
    this.video = video;

    if (window._paq && typeof window._paq.push === 'function') {
      window._paq.push(['MediaAnalytics::scanForMedia']);
    }
  }

  attach() {
    try {
      this.source = attachSource(this.video, this.url, {
        provider: this.provider,
        lazy: !this.autoplay && !this.startWithSound,
        onError: this.onError,
      });
    } catch (err) {
      console.error('mad-video: ' + err.message);
      this.source = { hls: null, startLoad: noop, destroy: noop };
      this.onError();
    }
  }

  /* ─────────────── Events ─────────────── */

  bindEvents() {
    var video = this.video;
    var opts = { signal: this.events.signal };
    var on = function (el, type, handler) {
      if (el) el.addEventListener(type, handler, opts);
    };

    on(video, 'click', () => this.onSurfaceClick());
    on(this.cue, 'click', () => this.unmuteFromAutoplay());
    on(this.playButton, 'click', () => this.togglePlay());
    on(this.centerPlay, 'click', () => this.togglePlay());
    on(this.muteButton, 'click', () => this.toggleMute());
    on(this.volumeSlider, 'input', () => this.setVolume(parseFloat(this.volumeSlider.value)));

    // Progress: 'input' while dragging, 'change' on release
    on(this.progress, 'input', () => {
      this.dragging = true;
      this.seek(parseFloat(this.progress.value));
    });
    on(this.progress, 'change', () => { this.dragging = false; });

    if (this.fullscreenCtrl && !this.fullscreenSupported()) {
      this.fullscreenCtrl.remove(); // js/slots.js skips controls that are gone
      this.fullscreenCtrl = this.fullscreenButton = null;
    }
    if (this.fullscreenButton) {
      on(this.fullscreenButton, 'click', () => this.toggleFullscreen());
      on(document, 'fullscreenchange', () => this.updateUi());
      on(document, 'webkitfullscreenchange', () => this.updateUi());
    }

    // Touch + auto-hide: remember how the viewer interacts; taps on visible controls keep them visible
    on(this.root, 'pointerdown', (e) => {
      this.lastPointerType = e.pointerType;
      if (e.pointerType === 'touch' && this.root.classList.contains('is-ui-visible')) {
        this.revealUi();
      }
    });

    // Keyboard on the focused video: space / k = play/pause, m = mute
    on(video, 'keydown', (e) => {
      this.lastPointerType = '';
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        this.onSurfaceClick();
      } else if (e.key === 'm') {
        this.toggleMute();
      }
    });

    on(video, 'play', () => {
      this.source.startLoad(); // lazy HLS: fetch segments only now
      this.started = true;
      this.trackProgress();
    });
    on(video, 'playing', () => this.onPlaying());
    on(video, 'waiting', () => this.root.classList.add('is-buffering'));
    ['playing', 'pause', 'canplay', 'ended'].forEach((type) => {
      on(video, type, () => this.root.classList.remove('is-buffering'));
    });
    ['play', 'pause', 'ended', 'volumechange'].forEach((type) => {
      on(video, type, () => this.updateUi());
    });

    // Seeking needs a duration (lazy HLS has none until the manifest is parsed)
    on(video, 'loadedmetadata', () => {
      if (this.progress && this.seekable) this.progress.disabled = false;
    });
    on(video, 'timeupdate', () => {
      if (video.paused) this.renderProgress(); // the rAF loop only runs while playing
    });
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      if (this.autoplay) this.startMutedAutoplay();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        var ratio = entry.isIntersecting ? entry.intersectionRatio : 0;

        if (ratio >= VISIBLE_RATIO) {
          if (!this.started && this.autoplay) {
            this.startMutedAutoplay();
          } else if (this.pausedOffscreen) {
            this.pausedOffscreen = false;
            this.play().catch(noop);
          }
        } else if (ratio < HIDDEN_RATIO && this.pauseOffscreen && this.isPlaying()) {
          this.pausedOffscreen = true;
          this.video.pause();
        }
      });
    }, { threshold: [0, HIDDEN_RATIO, VISIBLE_RATIO] });

    this.observer.observe(this.root);
  }

  /* ─────────────── Playback ─────────────── */

  isPlaying() {
    return !this.video.paused && !this.video.ended;
  }

  play() {
    var promise = this.video.play();
    return promise && promise.catch ? promise : Promise.resolve();
  }

  startMutedAutoplay() {
    this.video.muted = true;
    this.mutedByAutoplay = true;
    this.play().catch(() => {
      // Autoplay blocked (e.g. iOS low power mode): fall back to the play buttons
      this.mutedByAutoplay = false;
      this.updateUi();
    });
    this.updateUi();
  }

  /**
   * Start with sound. Must run synchronously inside a click handler (iOS).
   */
  playWithSound() {
    this.video.muted = false;
    this.video.volume = this.volume;
    this.play().catch(() => {
      // Sound not allowed without a direct tap: play muted and show the cue instead
      this.startMutedAutoplay();
    });
  }

  unmuteFromAutoplay() {
    this.mutedByAutoplay = false;
    if (this.restartOnUnmute) {
      this.video.currentTime = 0;
    }
    this.video.muted = false;
    this.video.volume = this.volume || 0.5; // the video's "Default volume" (desktop; iOS ignores it)
    this.play().catch(noop);
    this.updateUi();
  }

  onSurfaceClick() {
    if (this.mutedByAutoplay) {
      this.unmuteFromAutoplay();
      return;
    }
    // Touch + auto-hide: a tap on the hidden controls only brings them back
    if (this.autohide && this.lastPointerType === 'touch' && this.isPlaying() && !this.root.classList.contains('is-ui-visible')) {
      this.revealUi();
      return;
    }
    this.togglePlay();
  }

  togglePlay() {
    this.pausedOffscreen = false;
    if (this.video.paused || this.video.ended) {
      if (!this.started && !this.mutedByAutoplay) {
        this.playWithSound(); // first start by the viewer: with sound
      } else {
        this.play().catch(noop);
      }
    } else {
      this.video.pause();
    }
  }

  toggleMute() {
    if (this.mutedByAutoplay) {
      this.unmuteFromAutoplay();
      return;
    }
    if (this.video.muted || this.video.volume === 0) {
      this.video.muted = false;
      this.video.volume = this.volume || 0.5;
    } else {
      this.video.muted = true;
    }
  }

  setVolume(value) {
    if (this.mutedByAutoplay && value > 0) {
      this.unmuteFromAutoplay();
    }
    this.video.volume = value;
    this.video.muted = value === 0;
    if (value > 0) this.volume = value;
  }

  /* ─────────────── Progress ─────────────── */

  // timeupdate fires ~4×/s — too choppy for a moving bar, so follow playback per frame
  trackProgress() {
    if (!this.progress) return;
    cancelAnimationFrame(this.progressFrame);

    var tick = () => {
      this.renderProgress();
      if (this.isPlaying()) this.progressFrame = requestAnimationFrame(tick);
    };
    tick();
  }

  renderProgress() {
    if (!this.progress) return;

    var duration = this.video.duration;
    var percent = isFinite(duration) && duration > 0 ? (this.video.currentTime / duration) * 100 : 0;

    if (!this.dragging) {
      this.progress.value = String(percent);
      this.progress.style.setProperty('--mad-video-progress', percent + '%');
    }
  }

  seek(percent) {
    var duration = this.video.duration;
    if (!isFinite(duration)) return;

    this.video.currentTime = (percent / 100) * duration;
    this.progress.style.setProperty('--mad-video-progress', percent + '%');
  }

  /* ─────────────── Fullscreen ─────────────── */

  fullscreenSupported() {
    var el = this.root;
    return !!(el.requestFullscreen || el.webkitRequestFullscreen || this.video.webkitEnterFullscreen);
  }

  isFullscreen() {
    var current = document.fullscreenElement || document.webkitFullscreenElement;
    return current === this.root;
  }

  toggleFullscreen() {
    if (this.isFullscreen()) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }

    var el = this.root;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (this.video.webkitEnterFullscreen) {
      this.video.webkitEnterFullscreen(); // iPhone: native fullscreen player
    }
  }

  /* ─────────────── UI state ─────────────── */

  revealUi() {
    this.root.classList.add('is-ui-visible');
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => this.root.classList.remove('is-ui-visible'), UI_REVEAL_MS);
  }

  setLabel(el, key) {
    if (el) el.setAttribute('aria-label', this.labels[key]);
  }

  updateUi() {
    var video = this.video;
    var playing = this.isPlaying();
    var muted = video.muted || video.volume === 0;
    var ended = video.ended && !this.loop;
    var fullscreen = this.isFullscreen();
    var classes = this.root.classList;

    classes.toggle('is-started', this.started);
    classes.toggle('is-playing', playing);
    classes.toggle('is-paused', !playing);
    classes.toggle('is-muted', muted);
    classes.toggle('is-ended', ended);
    classes.toggle('is-fullscreen', fullscreen);

    this.setLabel(this.playButton, playing ? 'pause' : (ended ? 'replay' : 'play'));
    this.setLabel(this.centerPlay, ended ? 'replay' : 'play');
    this.setLabel(this.muteButton, muted ? 'unmute' : 'mute');
    this.setLabel(this.fullscreenButton, fullscreen ? 'exitFullscreen' : 'fullscreen');

    if (this.muteButton) {
      this.muteButton.setAttribute('aria-pressed', muted ? 'true' : 'false');
    }

    if (this.volumeSlider) {
      var level = muted ? 0 : video.volume;
      this.volumeSlider.value = String(level);
      this.volumeSlider.style.setProperty('--mad-video-volume-level', Math.round(level * 100) + '%');
    }

    if (this.cue) {
      this.cue.hidden = !(this.mutedByAutoplay && this.unmuteCue);
    }
  }

  /* ─────────────── Cleanup ─────────────── */

  destroy() {
    this.events.abort();
    cancelAnimationFrame(this.progressFrame);
    clearTimeout(this.revealTimer);
    if (this.observer) this.observer.disconnect();

    try { this.source.destroy(); } catch (e) {}
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.mount.innerHTML = '';

    if (this.cue) this.cue.hidden = true;
    STATE_CLASSES.forEach((name) => this.root.classList.remove(name));
  }
}

module.exports = MinimalPlayer;
